import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import { analytics } from '../../../../services/analytics';
import { postRequest } from '../../../../services/api';
import type { ApiResponse } from '../../../../services/types';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import Button from '../../../buttons/Button';
import { Dialog } from '../../../Dialog/Dialog';
import { FormInput } from '../../../FormInput/FormInput';

import styles from './PromoCodeInputDialog.module.scss';
interface ValidatePromoCodeResponse {
  isValid: boolean;
  eventTriggerId: string;
}

const PromoCodeInputDialog = observer(() => {
  const { uiStore, eventTriggerStore } = useStores();
  const { t } = useTranslation();
  const isOpen = uiStore.openDialog.dialog === EnumOpenDialog.isOpenPromoCodeDialog;
  const [promoCode, setPromoCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffectOnce(() => {
    void analytics.sendEvent('promo_code', {
      event_category: 'promo_code',
      event_action: 'impression',
    });
  });
  const handleClose = () => {
    uiStore.setCloseDialog();
  };

  const sendApplyPromoCodeEvent = (success: boolean) => {
    void analytics.sendEvent('apply_promo_code', {
      event_category: 'promo_code',
      event_type: 'status',
      event_value: success ? 'valid' : 'invalid',
      event_action: 'click',
      cta_button: 'apply',
    });
  };

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const response = await postRequest<ApiResponse<ValidatePromoCodeResponse>>('/v2/promo-code/validate', {
        promoCode,
      });
      const { isValid } = response.data.data;
      if (isValid) {
        await eventTriggerStore.fetchWithPromoCode(promoCode);
        sendApplyPromoCodeEvent(true);
        handleClose();
        setPromoCode('');
      } else {
        sendApplyPromoCodeEvent(false);
        setError(t('promoCode.errors.invalidPromoCode'));
        setIsSubmitting(false);
      }
    } catch (err) {
      sendApplyPromoCodeEvent(false);
      setError(t('promoCode.errors.invalidPromoCode'));
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      void analytics.sendEvent('promo_code_screen', {
        action: 'impression',
        event_category: 'promocode',
      });
    }
  }, [isOpen]);

  return (
    <div className={styles.promoCodeDialogContainer}>
      <Dialog
        classNameBody={styles.dialogBody}
        classNameParent={styles.promoCodeDialog}
        isOpen={isOpen}
        newDialogStyleEnabled={true}
        title={t('crownLounge.promoCode')}
        onClose={handleClose}
      >
        <div className={styles.inner}>
          <div className={styles.content}>
            <div className={styles.inputContainer}>
              <FormInput
                autoFocus
                className={error ? styles.inputError : ''}
                dataTestId="promo-code-input"
                error={error}
                maxLength={20}
                placeholder={t('forms.enterPromoCode')}
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.trim());
                  if (error) setError('');
                }}
              />
            </div>
            <div className={styles.buttonContainer}>
              <Button
                btnType={error ? 'locked' : 'main'}
                data-testid="promo-code-submit-btn"
                disabled={isSubmitting || !promoCode.trim() || !!error}
                onClick={handleSubmit}
              >
                {isSubmitting ? t('buttons.applying') : t('common.applyUpperCase')}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default PromoCodeInputDialog;
